import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { X, Check, Search, Image as ImageIcon, Video } from 'lucide-react';
import { mediaApi } from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';

const PAGE_SIZE = 50;

export interface PickedMedia {
  key: string;
  isVideo: boolean;
}

interface Props {
  onClose: () => void;
  onSelect: (items: PickedMedia[]) => void;
}

/** Modal that lets the user pick existing assets from the workspace Media
 *  Library (uploads, AI-generated images, Canva exports) to attach to a post. */
export function MediaLibraryPicker({ onClose, onSelect }: Props) {
  const { getToken } = useAuth();
  const { currentWorkspace } = useAppStore();
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['media-picker', currentWorkspace?.id, typeFilter, search],
    queryFn: async ({ pageParam }) => {
      const token = await getToken();
      return mediaApi.list(token!, {
        workspaceId: currentWorkspace!.id,
        type: typeFilter || undefined,
        search: search || undefined,
        limit: PAGE_SIZE,
        offset: pageParam,
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.media.length < PAGE_SIZE
        ? undefined
        : allPages.reduce((n, p) => n + p.media.length, 0),
    enabled: !!currentWorkspace,
  });

  // Flattened across loaded pages, so selections from later pages are kept on confirm.
  const items = data?.pages.flatMap((p) => p.media) ?? [];
  const toggle = (key: string) => setSelected((p) => ({ ...p, [key]: !p[key] }));
  const chosenCount = Object.values(selected).filter(Boolean).length;

  const confirm = () => {
    const picked: PickedMedia[] = items
      .filter((i) => selected[i.r2_key])
      .map((i) => ({ key: i.r2_key, isVideo: (i.mime_type ?? '').startsWith('video') }));
    onSelect(picked);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground">Media Library</h2>
          <button
            onClick={onClose}
            className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted/40 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search files..."
              className="w-full bg-input border border-border rounded-md pl-9 pr-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
          <div className="flex gap-1">
            {['', 'image', 'video'].map((t) => (
              <button
                key={t}
                onClick={() => setTypeFilter(t)}
                className={cn(
                  'flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md transition-colors',
                  typeFilter === t ? 'bg-accent/10 text-accent' : 'text-muted-foreground hover:bg-muted/40'
                )}
              >
                {t === '' && 'All'}
                {t === 'image' && <><ImageIcon className="h-3 w-3" /> Images</>}
                {t === 'video' && <><Video className="h-3 w-3" /> Videos</>}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="aspect-square bg-muted rounded-lg animate-pulse" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-12">
              No media in this workspace yet. Upload or generate some first.
            </p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {items.map((item) => {
                const isImage = (item.mime_type ?? '').startsWith('image');
                const isSel = !!selected[item.r2_key];
                return (
                  <button
                    key={item.id}
                    onClick={() => toggle(item.r2_key)}
                    className={cn(
                      'group relative aspect-square rounded-lg overflow-hidden border transition-all',
                      isSel ? 'border-accent ring-2 ring-accent' : 'border-border hover:border-accent/40'
                    )}
                  >
                    {isImage ? (
                      <img
                        src={mediaApi.serveUrl(item.r2_key)}
                        alt={item.filename}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <video
                        src={mediaApi.serveUrl(item.r2_key)}
                        muted
                        preload="metadata"
                        className="w-full h-full object-cover"
                      />
                    )}
                    {isSel && (
                      <div className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full bg-accent text-accent-foreground flex items-center justify-center">
                        <Check className="h-3 w-3" />
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-black/60 px-1.5 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-[10px] text-white truncate">{item.filename}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {hasNextPage && (
            <div className="flex justify-center pt-4">
              <Button onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
                Load more
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <span className="text-xs text-muted-foreground">
            {chosenCount} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground rounded-md hover:bg-muted/40 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={confirm}
              disabled={chosenCount === 0}
              className="px-3 py-1.5 text-xs bg-accent text-accent-foreground rounded-md hover:bg-accent/90 disabled:opacity-50 transition-colors"
            >
              Add {chosenCount > 0 ? `(${chosenCount})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
